import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { awardDisplayTitle, awardMatchesRecord, displayAwardCatalogue, type AwardDefinition, type EarnedAward, type StudentAwardsSummary } from "@wmf/shared/studentAwards";
import { AWARD_EVIDENCE_EVENT, send } from "../api.js";
import { useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";
import { Pagination, usePagination } from "../Pagination.js";
import { Drawer } from "../ui/Dialog.js";
import { AwardMedal } from "./AwardMedal.js";
export { AwardMedal } from "./AwardMedal.js";
import "./studentAwards.css";

const FOCUS_REFRESH_INTERVAL_MS = 30_000;
const AWARD_HISTORY_PAGE_SIZE = 20;

/** Mounted only when the server advertises the feature. Practice remains saved
 * if reconciliation is temporarily unavailable; opening Awards retries it. */
export function AwardTracker({ studentId }: { studentId: string }) {
  useEffect(() => {
    let stopped = false, running = false, again = false;
    let lastRefreshAt = Number.NEGATIVE_INFINITY;
    const refresh = async () => {
      if (stopped) return;
      if (running) { again = true; return; }
      running = true;
      lastRefreshAt = Date.now();
      try { await send("POST", `/me/students/${encodeURIComponent(studentId)}/awards/refresh`, {}); }
      catch { /* The Awards page exposes a retry and the actual error. */ }
      finally {
        running = false;
        if (again && !stopped) { again = false; void refresh(); }
      }
    };
    const evidence = (event: Event) => {
      const path = (event as CustomEvent<{ path: string }>).detail?.path;
      if (path?.startsWith(`/me/students/${encodeURIComponent(studentId)}/`)) void refresh();
    };
    const focus = () => {
      if (document.visibilityState === "visible" && Date.now() - lastRefreshAt >= FOCUS_REFRESH_INTERVAL_MS) void refresh();
    };
    void refresh();
    window.addEventListener(AWARD_EVIDENCE_EVENT, evidence);
    window.addEventListener("focus", focus);
    return () => { stopped = true; window.removeEventListener(AWARD_EVIDENCE_EVENT, evidence); window.removeEventListener("focus", focus); };
  }, [studentId]);
  return null;
}

export function StudentAwards({ studentId, loadingStudent = false }: { studentId: string | null; loadingStudent?: boolean }) {
  const { me } = useAuth();
  if (me?.studentAwardsEnabled !== true) return <Navigate to="/home" replace/>;
  if (!studentId) return loadingStudent ? <p role="status">Loading your awards…</p> : <Navigate to="/home" replace/>;
  return <EnabledAwards key={studentId} studentId={studentId}/>;
}

function EnabledAwards({ studentId }: { studentId: string }) {
  const base = `/me/students/${encodeURIComponent(studentId)}/awards`;
  const state = useAsync(() => send<StudentAwardsSummary>("POST", `${base}/refresh`, {}), [studentId]);
  useEffect(() => {
    const reported = (event: Event) => {
      if ((event as CustomEvent<{ path: string }>).detail?.path === `/me/students/${encodeURIComponent(studentId)}/bug-report`) state.reload();
    };
    window.addEventListener(AWARD_EVIDENCE_EVENT, reported);
    return () => window.removeEventListener(AWARD_EVIDENCE_EVENT, reported);
  }, [studentId, state.reload]);
  const [detail, setDetail] = useState<{ definition: AwardDefinition; edition?: EarnedAward } | null>(null);
  const [target, setTarget] = useState("");
  const [targetStatus, setTargetStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const data = state.data;
  const date = (value: string) => new Intl.DateTimeFormat("en-GB", { timeZone: data?.timezone ?? "Europe/London", dateStyle: "medium" }).format(new Date(value));
  const earnedFor = (definition: AwardDefinition) => data?.earned.filter(value => awardMatchesRecord(definition, value)) ?? [];
  const catalogue = useMemo(() => displayAwardCatalogue(data?.catalogue ?? []), [data?.catalogue]);
  const earnedKeys = new Set(data?.earned.map(value => value.key));
  const detailRecords = detail && !detail.edition ? earnedFor(detail.definition) : [];
  const detailCollectionKey = detail ? `${studentId}:${detail.definition.receiptKey ?? detail.definition.key}` : null;
  const detailPagination = usePagination(detailRecords, AWARD_HISTORY_PAGE_SIZE, detailCollectionKey);
  const historySummaryRef = useRef<HTMLParagraphElement>(null);
  const previousHistoryPage = useRef({ identity: detailCollectionKey, page: detailPagination.page });
  useEffect(() => {
    const previous = previousHistoryPage.current;
    const changedWithinCollection = previous.identity === detailCollectionKey && previous.page !== detailPagination.page;
    previousHistoryPage.current = { identity: detailCollectionKey, page: detailPagination.page };
    if (!changedWithinCollection) return;
    const drawer = historySummaryRef.current?.closest<HTMLElement>('[role="dialog"]');
    if (drawer) drawer.scrollTop = 0;
    historySummaryRef.current?.focus({ preventScroll: true });
  }, [detailCollectionKey, detailPagination.page]);
  async function saveTarget() {
    setSaving(true); setTargetStatus("");
    try {
      const result = await send<{ minutes: number; startsOn: string }>("PUT", `${base}/weekly-target`, { minutes: Number(target) });
      setTargetStatus(`Your ${result.minutes} minute weekly target starts on ${date(`${result.startsOn}T12:00:00Z`)}.`);
      state.reload();
    } catch (error) { setTargetStatus(error instanceof Error ? error.message : "Could not save your target."); }
    finally { setSaving(false); }
  }
  const renderMedal = (definition: AwardDefinition, edition?: EarnedAward) => {
    const records = edition ? [edition] : earnedFor(definition);
    return <button type="button" className="quiet award-collection-item" key={edition?.id ?? definition.key} onClick={() => setDetail({ definition, edition })}>
      <AwardMedal definition={definition} earned={records.length > 0}/>
      <strong>{edition?.title ?? definition.title}</strong>
      <span className="small muted">{records.length ? `${records.length > 1 ? `${records.length} earned · ` : "Earned "}${date(records[0]!.earnedAt)}` : "To earn"}</span>
    </button>;
  };
  return <div className="stack student-awards-page">
    <header><h1>Awards</h1><p className="muted">Your practice, progress and personal achievements.</p></header>
    {state.loading && !data && <p role="status">Loading your awards…</p>}
    {state.error && <div className="error" role="alert">{state.error} <button type="button" onClick={state.reload}>Try again</button></div>}
    {data && <>
      <section className="panel" aria-label="Practice achievements">
        <div className="award-practice-stats">
          <div><span className="small muted">Current streak</span><strong>{data.stats.currentStreak} days</strong></div>
          <div><span className="small muted">Longest streak</span><strong>{data.stats.longestStreak} days</strong></div>
          <div><span className="small muted">Practice this week</span><strong>{data.stats.thisWeekMinutes} minutes</strong></div>
          <div><span className="small muted">Tutor-confirmed tasks</span><strong>{data.stats.completedTasks}</strong></div>
        </div>
        <p className="small muted">A streak day needs one practice session of at least 10 minutes. Your longest streak stays recorded after a break.</p>
        <p className="small">Perfect month: {data.stats.thisMonthQualifyingDays} of {data.stats.thisMonthCalendarDays} days. Monthly challenge: {Math.min(data.stats.thisMonthDays, 8)} of 8 practice days.</p>
        <p className="small">Your weekly practice target is {data.weeklyTargetMinutes} minutes. You have reached a weekly target in {data.stats.targetWeeks} weeks.</p>
        {data.nextWeeklyTarget && <p className="small">Your next weekly target is {data.nextWeeklyTarget.minutes} minutes, starting {date(`${data.nextWeeklyTarget.week}T12:00:00Z`)}.</p>}
        <details><summary>Change your weekly target</summary><form className="stack" onSubmit={event => { event.preventDefault(); void saveTarget(); }}>
          <label htmlFor="award-weekly-target">Minutes per week</label><input id="award-weekly-target" type="number" min="1" max="3360" required value={target} placeholder={String(data.weeklyTargetMinutes)} disabled={saving} onChange={event => setTarget(event.target.value)}/>
          <p className="small muted">Changes start next Monday. Earlier weeks keep the target you had then.</p>
          <button type="submit" disabled={saving || !target}>{saving ? "Saving…" : "Save target"}</button>
          {targetStatus && <p role="status" className="small">{targetStatus}</p>}
        </form></details>
      </section>
      <section aria-labelledby="earned-awards-heading"><h2 id="earned-awards-heading">Your collection</h2>
        {!earnedKeys.size && <p className="muted">Your medals will appear here as you earn them.</p>}
        <div className="award-collection">{catalogue.filter(definition => earnedFor(definition).length > 0).flatMap(definition => definition.month
          ? earnedFor(definition).map(edition => renderMedal(definition, edition)) : [renderMedal(definition)])}</div>
      </section>
      <section aria-labelledby="next-awards-heading"><h2 id="next-awards-heading">More to earn</h2><div className="award-collection">{catalogue.filter(definition => !earnedFor(definition).length).map(definition => renderMedal(definition))}</div></section>
      {data.lessonAttendance.excludedSessions > 0 && <p className="small muted">{data.lessonAttendance.excludedSessions} attended {data.lessonAttendance.excludedSessions === 1 ? "session is" : "sessions are"} excluded from lesson milestone awards.</p>}
    </>}
    {detail && <Drawer title={detail.edition?.title ?? detail.definition.title} onClose={() => setDetail(null)} size="medium">
      <div className="stack"><AwardMedal definition={detail.definition} earned={earnedFor(detail.definition).length > 0}/><p>{detail.definition.description}</p>
        {detail.edition ? <p>Earned {date(detail.edition.earnedAt)}.</p> : earnedFor(detail.definition).length > 0 ? <><p>{earnedFor(detail.definition).length} {earnedFor(detail.definition).length === 1 ? "medal" : "medals"} earned.</p>
          <ul>{detailPagination.items.map((record: EarnedAward) => <li key={record.id}>{awardDisplayTitle(record)} · {date(record.earnedAt)}{record.reason && <><br/>{record.reason}</>}</li>)}</ul>
          {detailRecords.length > AWARD_HISTORY_PAGE_SIZE && <>
            <p ref={historySummaryRef} tabIndex={-1} className="small muted" role="status">Showing medals {(detailPagination.page - 1) * AWARD_HISTORY_PAGE_SIZE + 1}–{Math.min(detailPagination.page * AWARD_HISTORY_PAGE_SIZE, detailRecords.length)} of {detailRecords.length}.</p>
            <Pagination
              page={detailPagination.page}
              pages={detailPagination.pages}
              onPrevious={detailPagination.previous}
              onNext={detailPagination.next}
              ariaLabel="Award history pages"
              previousAriaLabel="Previous award history page"
              nextAriaLabel="Next award history page"
            />
          </>}</> : <p className="muted">Keep going — this one is still to earn.</p>}
      </div>
    </Drawer>}
  </div>;
}
